import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../app/authStore';
import { ApiError, API_BASE, apiUrl, isApiPath } from '../lib/api';
import { Button, Card, PageHeader } from '../components/ui';
import { Field, Notice, PasswordInput } from '../components/form';

/**
 * 初始口令的强制改密页。
 *
 * 未改密之前 Worker 会对所有业务接口返回 403，因此这一页是唯一能走的出口。
 * 改密后当前设备会被**重签会话**（而不是被踢下线）—— 用户不该因为一次安全操作而重新登录。
 */
export function PasswordPage() {
  const session = useAuthStore((state) => state.session);
  const changePassword = useAuthStore((state) => state.changePassword);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // 从登录页带过来的「改完去哪儿」。没有就回概览。
  const continueTo = params.get('continue') ?? '/';

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const forced = session?.mustChangePassword === true;

  async function submit() {
    setError(null);
    setFields({});

    if (next !== confirm) {
      setFields({ newPassword: '两次输入的新口令不一致' });
      return;
    }

    setBusy(true);
    try {
      await changePassword(current, next);
      // 改完后如果原本是要继续走接口流程（OIDC），整页跳回接口域名接着走，
      // 否则之前那次授权请求就断在这里了。
      if (API_BASE !== '' && isApiPath(continueTo)) {
        window.location.replace(apiUrl(continueTo));
        return;
      }
      navigate(continueTo, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFields(caught.fieldErrors);
        setError(caught.fieldErrors.newPassword ?? caught.fieldErrors.currentPassword ?? null);
      } else {
        setError('改密失败，请稍后重试');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-md">
        <PageHeader
          title="修改口令"
          description="口令至少 10 位，需同时包含字母与数字。"
        />
        <Card>
          {forced ? (
            <div className="mb-4">
              <Notice tone="warn">
                当前账号仍在使用初始口令。初始口令写在部署配置里，属于共享秘密 —— 不改就等于没有访问控制。
              </Notice>
            </div>
          ) : null}

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Field label="当前口令" htmlFor="current" error={fields.currentPassword}>
              <PasswordInput
                id="current"
                value={current}
                onChange={setCurrent}
                autoComplete="current-password"
                required
              />
            </Field>
            <Field
              label="新口令"
              htmlFor="next"
              error={fields.newPassword}
              hint="至少 10 位，含字母与数字，且不能与邮箱相同"
            >
              <PasswordInput id="next" value={next} onChange={setNext} autoComplete="new-password" required />
            </Field>
            <Field label="确认新口令" htmlFor="confirm">
              <PasswordInput id="confirm" value={confirm} onChange={setConfirm} autoComplete="new-password" required />
            </Field>

            {error && !fields.newPassword && !fields.currentPassword ? (
              <p className="text-[13px] text-danger" role="alert">
                {error}
              </p>
            ) : null}

            <p className="text-xs text-fg-muted">
              改密后，其它设备上的登录会立即失效，当前设备保持登录。
            </p>

            <Button type="submit" variant="primary" block loading={busy}>
              保存并继续
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
